import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import {
    ClubNavItem,
    Search,
    SeasonNavItem
} from 'widgets/player-awards-tracker/js/components/Leaderboard/components/nav-items';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { BrownlowPredictor } from './BrownlowPredictor';
import { leaderboardNavSelector } from 'common/store/modules/brownlow-tracker/leaderboard/selectors';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useLeaderboardNavSync } from 'widgets/player-awards-tracker/js/components/Leaderboard/hooks/use-leaderboard-nav-sync';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { UPDATE_NAV_ITEM } from 'common/store/modules/brownlow-tracker/leaderboard/actions';
import { getTranslation } from 'common/react/utils/translations';

const NavWrapper = ({ children }) => (
    <div className="competition-nav filter-dropdown">{children}</div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const BPLeaderboard = ({
    seasonsToShow,
    seasonId,
    compId,
    seasonPid,
    title
}) => {
    const classPrefix = 'brownlow-leaderboard-predictor brownlow-leaderboard';
    const windowSize = useWindowSizeV2();

    const nav = useSelector(leaderboardNavSelector);
    const dispatch = useDispatch();
    const options = useCompetitionNavigationOptions(nav);

    options.defaultSeason = seasonPid ?? seasonPid;
    options.seasonsToShow = seasonsToShow ?? seasonsToShow;
    useLeaderboardNavSync(options);

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    const leaderboardRef = useRef();

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: nav,
        options,
        filtersFields: ['season', 'team'],
        filtersRef: leaderboardRef
    });

    const getInitialSeasonId = () => {
        return options.defaultSeason
            ? options.seasons?.find(
                  (season) => season.providerId === options.defaultSeason
              )?.id
            : options.seasons?.[0]?.id;
    };

    const getInitialTeamId = () => {
        return options.teams?.[0]?.id || -1;
    };

    const hasChangedFromDefaults = () => {
        if (!nav?.season || !nav?.team) {
            return false;
        }

        const initialSeasonId = getInitialSeasonId();
        const initialTeamId = getInitialTeamId();

        const isSeasonChanged = nav?.season?.id !== initialSeasonId;
        const isTeamChanged = nav?.team?.id !== initialTeamId;

        return isSeasonChanged || isTeamChanged;
    };

    const resetToDefaults = () => {
        const initialSeasonId = getInitialSeasonId();
        const initialTeamId = getInitialTeamId();

        if (nav?.season?.id !== initialSeasonId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'season',
                    value: { id: initialSeasonId }
                })
            );
        }
        if (nav?.team?.id !== initialTeamId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'team',
                    value: { id: initialTeamId }
                })
            );
        }
    };

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const selectedSeasonPid = useMemo(() => {
        return options?.seasons?.find((season) => season.id === nav.season?.id)
            ?.providerId;
    }, [nav, options]);

    return (
        <div ref={leaderboardRef} className={classPrefix}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <div className="player-awards-wrapper">
                            <SeasonNavItem
                                classes="shrink bp-season-nav-item"
                                seasonsToShow={seasonsToShow}
                                seasonId={seasonId}
                                compId={compId}
                                title={title}
                            />
                            <ClubNavItem
                                title={title}
                                classes={'clubnavitem'}
                            />
                        </div>
                        {hasChangedFromDefaults() && !showResponsiveItems ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <StatsSection.InfoButton
                                    tooltipContent="BPLeaderboardTooltip"
                                    className="p-none"
                                    seasonPid={selectedSeasonPid}
                                />
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults() && showResponsiveItems ? (
                        <StatsSection.NavFilterItems>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}

                    <StatsSection.SettingsButton>
                        <div className={`${classPrefix}-nav__extras`}>
                            <Search />
                        </div>
                        <StatsSection.InfoButton
                            tooltipContent="BPLeaderboardTooltip"
                            className="p-none"
                            seasonPid={selectedSeasonPid}
                        />
                        <SettingsButton />
                    </StatsSection.SettingsButton>

                    {showResponsiveItems ? (
                        <StatsSection.SearchBarMobile>
                            <div className={`${classPrefix}-nav__extras`}>
                                <Search />
                            </div>
                        </StatsSection.SearchBarMobile>
                    ) : null}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentBody>
                        <BrownlowPredictor seasonId={seasonId} />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

BPLeaderboard.propTypes = {
    seasonsToShow: PropTypes.string,
    seasonId: PropTypes.string,
    compId: PropTypes.string,
    seasonPid: PropTypes.string,
    title: PropTypes.string
};
